import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * BillingPanel — Account → Billing
 *
 * Renders the live membership state from /api/billing/. Every price, date and
 * card shown here comes from Stripe via the backend; nothing is hardcoded.
 *
 * Usage:  <BillingPanel />   (inside the Billing tab of SettingsPage)
 */

const API = process.env.REACT_APP_API_URL;
const NAVY = "#1a2744";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

async function api(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}/api/billing/${path}`, {
    method,
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || data.message || "Request failed.");
  return data;
}

const money = (cents, currency = "usd") =>
  cents == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(cents / 100);

const longDate = (iso) =>
  !iso
    ? "—"
    : new Date(iso).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });

const shortDate = (iso) =>
  !iso
    ? "—"
    : new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const cardBrand = (brand) =>
  ({ visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover" }[
    (brand || "").toLowerCase()
  ] || (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card"));

/* ─── small pieces ───────────────────────────────────────────────────────── */

function StatusPill({ sub }) {
  const map = {
    trialing: ["Trial", "bg-blue-50 text-blue-700 border-blue-200"],
    active: ["Active", "bg-green-50 text-green-700 border-green-200"],
    past_due: ["Payment failed", "bg-amber-50 text-amber-700 border-amber-200"],
    unpaid: ["Payment failed", "bg-amber-50 text-amber-700 border-amber-200"],
    canceled: ["Ended", "bg-gray-100 text-gray-600 border-gray-200"],
    none: ["Not subscribed", "bg-gray-100 text-gray-600 border-gray-200"],
  };
  let [label, tone] = map[sub.status] || map.none;
  if (sub.cancel_at_period_end) {
    label = "Cancels " + shortDate(sub.access_until);
    tone = "bg-amber-50 text-amber-700 border-amber-200";
  }
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${tone}`}>{label}</span>
  );
}

function Card({ title, action, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-900">{title}</h3>
        {action}
      </div>
      <div className="px-5 sm:px-6 py-5">{children}</div>
    </section>
  );
}

function Notice({ tone = "info", children, onDismiss }) {
  const tones = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    error: "bg-red-50 border-red-200 text-red-700",
    success: "bg-green-50 border-green-200 text-green-800",
  };
  return (
    <div className={`flex items-start gap-3 border rounded-xl px-4 py-3 text-sm ${tones[tone]}`}>
      <div className="flex-1">{children}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-current opacity-60 hover:opacity-100 text-lg leading-none">
          ×
        </button>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

function Button({ variant = "primary", busy, children, className = "", ...rest }) {
  const base =
    "inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500";
  const styles = {
    primary: "text-white hover:opacity-90",
    secondary: "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50",
    quiet: "text-gray-500 hover:text-gray-800 hover:bg-gray-50",
    danger: "bg-white border border-red-200 text-red-600 hover:bg-red-50",
    dangerSolid: "bg-red-600 text-white hover:bg-red-700",
  };
  return (
    <button
      {...rest}
      disabled={busy || rest.disabled}
      style={variant === "primary" ? { background: NAVY } : undefined}
      className={`${base} ${styles[variant]} ${className}`}
    >
      {busy && <Spinner />}
      {children}
    </button>
  );
}

/* ─── cycle chooser ──────────────────────────────────────────────────────── */

function CycleChooser({ plan, value, onChange, currentInterval }) {
  const options = [
    {
      key: "monthly",
      interval: "month",
      label: "Monthly",
      amount: plan?.monthly?.amount_cents,
      per: "per month",
      note: "Billed every month. Cancel any time.",
      available: plan?.monthly?.available,
    },
    {
      key: "yearly",
      interval: "year",
      label: "Yearly",
      amount: plan?.yearly?.amount_cents,
      per: "per year",
      note:
        plan?.yearly?.savings_cents > 0
          ? `${money(plan.yearly.savings_cents, plan.currency)} less than 12 monthly payments.`
          : "Billed once a year.",
      available: plan?.yearly?.available,
    },
  ];

  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {options.map((opt) => {
        const selected = value === opt.key;
        const isCurrent = currentInterval === opt.interval;
        return (
          <button
            key={opt.key}
            type="button"
            disabled={!opt.available}
            onClick={() => onChange(opt.key)}
            aria-pressed={selected}
            className={`text-left border rounded-xl p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
              selected ? "border-transparent ring-2 bg-gray-50" : "border-gray-200 hover:border-gray-300"
            }`}
            style={selected ? { boxShadow: `0 0 0 2px ${NAVY}` } : undefined}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-bold text-gray-900">{opt.label}</span>
              {isCurrent && (
                <span className="text-[11px] font-semibold text-gray-500 border border-gray-200 rounded-full px-2 py-0.5">
                  Current
                </span>
              )}
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold text-gray-900">
                {money(opt.amount, plan?.currency)}
              </span>
              <span className="text-xs text-gray-500">{opt.per}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
              {opt.available ? opt.note : "Not available for this plan yet."}
            </p>
          </button>
        );
      })}
    </div>
  );
}

/* ─── cancel dialog ──────────────────────────────────────────────────────── */

function CancelDialog({ sub, busy, error, onConfirm, onClose }) {
  const endDate = longDate(sub.current_period_end);
  const trial = sub.in_trial;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-title"
    >
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-xl">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 id="cancel-title" className="text-base font-bold text-gray-900">
            Cancel your membership
          </h3>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm text-gray-600">
          <p>
            {trial
              ? `Your trial ends ${endDate}. Cancelling now means no charge is taken and access ends on that date.`
              : `Your membership stays active through ${endDate} — the term you've already paid for. No further charges will be taken.`}
          </p>

          <ul className="space-y-2 border border-gray-200 rounded-xl p-4 bg-gray-50">
            <li className="flex gap-2.5">
              <span className="text-gray-400">•</span>
              <span>
                Full access continues until <strong className="text-gray-900">{endDate}</strong>.
              </span>
            </li>
            <li className="flex gap-2.5">
              <span className="text-gray-400">•</span>
              <span>
                {trial
                  ? "You won't be charged at all."
                  : "The current term is not refunded or prorated."}
              </span>
            </li>
            <li className="flex gap-2.5">
              <span className="text-gray-400">•</span>
              <span>Resume any time before that date and billing continues uninterrupted.</span>
            </li>
            <li className="flex gap-2.5">
              <span className="text-gray-400">•</span>
              <span>
                After that date the account moves to the free tier. Your job history, documents and
                invoices stay in your account.
              </span>
            </li>
          </ul>

          {error && <Notice tone="error">{error}</Notice>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Keep membership
          </Button>
          <Button variant="dangerSolid" busy={busy} onClick={onConfirm}>
            Cancel membership
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── main panel ─────────────────────────────────────────────────────────── */

export default function BillingPanel() {
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [plan, setPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [choice, setChoice] = useState("monthly");
  const [showCancel, setShowCancel] = useState(false);
  const [cancelError, setCancelError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [subData, invoiceData] = await Promise.all([
        api("subscription/"),
        api("invoices/").catch(() => []),
      ]);
      setSub(subData);
      setPlan(subData.plan);
      setInvoices(Array.isArray(invoiceData) ? invoiceData : []);
      if (subData.billing_interval === "year") setChoice("yearly");
      if (!subData.plan) {
        const planData = await api("plan/").catch(() => null);
        if (planData) setPlan(planData);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Returning from Stripe Checkout or the portal.
  useEffect(() => {
    const result = params.get("checkout");
    if (!result) return;
    if (result === "success") setFlash("Membership confirmed. Welcome aboard.");
    if (result === "cancelled") setFlash("Checkout was closed. Nothing was charged.");
    params.delete("checkout");
    params.delete("session_id");
    setParams(params, { replace: true });
    // Webhooks land within a second or two; re-read shortly after.
    const t = setTimeout(load, 1500);
    return () => clearTimeout(t);
  }, [params, setParams, load]);

  const run = async (key, fn) => {
    setBusy(key);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const startCheckout = () =>
    run("checkout", async () => {
      const { url } = await api("checkout/", { method: "POST", body: { interval: choice } });
      window.location.href = url;
    });

  const openPortal = (intent) =>
    run(intent, async () => {
      const { url } = await api("portal/", { method: "POST", body: { intent } });
      window.location.href = url;
    });

  const switchInterval = () =>
    run("switch", async () => {
      const data = await api("change-interval/", { method: "POST", body: { interval: choice } });
      setSub(data.subscription);
      setPlan(data.subscription.plan);
      setFlash(data.message);
      load();
    });

  const resume = () =>
    run("resume", async () => {
      const data = await api("resume/", { method: "POST" });
      setSub(data.subscription);
      setFlash("Membership resumed. Billing continues as normal.");
    });

  const confirmCancel = async () => {
    setBusy("cancel");
    setCancelError("");
    try {
      const data = await api("cancel/", { method: "POST" });
      setSub(data.subscription);
      setShowCancel(false);
      setFlash(`Cancellation scheduled. Access continues until ${longDate(data.access_until)}.`);
    } catch (e) {
      setCancelError(e.message);
    } finally {
      setBusy("");
    }
  };

  const currency = plan?.currency || "usd";
  const subscribed = sub?.is_active && sub?.status !== "none";
  const intervalChanged = subscribed && !sub.cancel_at_period_end &&
    ((choice === "yearly" && sub.billing_interval !== "year") ||
      (choice === "monthly" && sub.billing_interval !== "month"));

  const renewalLine = useMemo(() => {
    if (!sub || !subscribed) return null;
    if (sub.cancel_at_period_end)
      return `Access ends ${longDate(sub.access_until)}. No further charges.`;
    if (sub.in_trial)
      return `Trial ends ${longDate(sub.trial_end)} — first charge of ${money(
        sub.renewal_amount_cents,
        currency
      )} on that date.`;
    if (sub.status === "past_due")
      return "The last payment didn't go through. Update the card on file to keep your membership active.";
    return `Renews ${longDate(sub.current_period_end)} for ${money(sub.renewal_amount_cents, currency)}.`;
  }, [sub, subscribed, currency]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-2xl h-32 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      {flash && (
        <Notice tone="success" onDismiss={() => setFlash("")}>
          {flash}
        </Notice>
      )}
      {error && (
        <Notice tone="error" onDismiss={() => setError("")}>
          {error}
        </Notice>
      )}
      {sub?.cancel_at_period_end && (
        <Notice tone="warn">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="flex-1">
              Your membership is scheduled to end on{" "}
              <strong>{longDate(sub.access_until)}</strong>. Resume before then and nothing changes.
            </span>
            <Button variant="secondary" busy={busy === "resume"} onClick={resume}>
              Resume membership
            </Button>
          </div>
        </Notice>
      )}
      {sub?.status === "past_due" && (
        <Notice tone="warn">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="flex-1">
              We couldn't charge the card on file. Update it to avoid losing access.
            </span>
            <Button
              variant="secondary"
              busy={busy === "payment_method"}
              onClick={() => openPortal("payment_method")}
            >
              Update card
            </Button>
          </div>
        </Notice>
      )}

      {/* Membership */}
      <Card title="Membership" action={sub && <StatusPill sub={sub} />}>
        {subscribed ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-xl font-bold text-gray-900">{plan?.name || "Membership"}</span>
              <span className="text-sm text-gray-500">
                {money(sub.renewal_amount_cents, currency)}{" "}
                {sub.billing_interval === "year" ? "per year" : "per month"}
              </span>
            </div>
            <p className="text-sm text-gray-600">{renewalLine}</p>
            {plan?.features?.length > 0 && (
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 pt-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                    <svg
                      className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {plan
                ? `${plan.name} unlocks the full platform for your account. ${
                    plan.trial_days && !sub?.has_used_trial
                      ? `Start with ${plan.trial_days} days free — your card isn't charged until the trial ends.`
                      : "Billing starts today and you can cancel any time."
                  }`
                : "No plan is configured for this account type yet. Contact support and we'll sort it out."}
            </p>
            {plan && (
              <>
                <CycleChooser plan={plan} value={choice} onChange={setChoice} currentInterval="" />
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <Button busy={busy === "checkout"} onClick={startCheckout}>
                    {plan.trial_days && !sub?.has_used_trial
                      ? `Start ${plan.trial_days}-day trial`
                      : "Start membership"}
                  </Button>
                  <span className="text-xs text-gray-500">
                    Secure checkout by Stripe. Cancel any time.
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Billing cycle */}
      {subscribed && plan && (
        <Card title="Billing cycle">
          <CycleChooser
            plan={plan}
            value={choice}
            onChange={setChoice}
            currentInterval={sub.billing_interval}
          />
          {intervalChanged && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-xs text-gray-500 flex-1 leading-relaxed">
                {choice === "yearly"
                  ? "Switching to yearly bills you today for the year, with the unused part of this month credited against it."
                  : "Switching to monthly takes effect from your next invoice. The unused part of the year is credited to upcoming invoices rather than refunded."}
              </p>
              <Button busy={busy === "switch"} onClick={switchInterval}>
                Switch to {choice}
              </Button>
            </div>
          )}
          {sub.cancel_at_period_end && (
            <p className="text-xs text-gray-500 mt-4">
              Resume your membership to change the billing cycle.
            </p>
          )}
        </Card>
      )}

      {/* Payment method */}
      <Card title="Payment method">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-11 h-8 border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 flex-shrink-0">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            {sub?.payment_method ? (
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {cardBrand(sub.payment_method.brand)} ending in {sub.payment_method.last4}
                </p>
                <p
                  className={`text-xs ${
                    sub.payment_method.expiring_soon ? "text-amber-600 font-medium" : "text-gray-500"
                  }`}
                >
                  Expires {String(sub.payment_method.exp_month).padStart(2, "0")}/
                  {String(sub.payment_method.exp_year).slice(-2)}
                  {sub.payment_method.expiring_soon && " — expiring soon"}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-semibold text-gray-900">No card on file</p>
                <p className="text-xs text-gray-500">
                  Add one to start or restart a membership.
                </p>
              </div>
            )}
          </div>
          <Button
            variant="secondary"
            busy={busy === "payment_method"}
            onClick={() => openPortal("payment_method")}
            disabled={!sub?.payment_method && !subscribed}
          >
            {sub?.payment_method ? "Update card" : "Add card"}
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-4">
          Card details are entered on Stripe's hosted page and never touch PilotLinkUSA servers.
        </p>
      </Card>

      {/* Billing history */}
      {invoices.length > 0 && (
        <Card
          title="Billing history"
          action={
            <Button variant="quiet" busy={busy === "manage"} onClick={() => openPortal("manage")}>
              Open billing portal
            </Button>
          }
        >
          <div className="divide-y divide-gray-100 -my-2">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center gap-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {shortDate(inv.created)}
                    {inv.number && <span className="text-gray-400 font-normal"> · {inv.number}</span>}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{inv.status}</p>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {money(inv.amount_paid_cents || inv.amount_due_cents, inv.currency)}
                </span>
                {inv.invoice_pdf && (
                  <a
                    href={inv.invoice_pdf}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                  >
                    PDF
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cancel */}
      {subscribed && !sub.cancel_at_period_end && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-1 pt-2">
          <p className="text-xs text-gray-500 flex-1">
            Cancelling keeps your access until {longDate(sub.current_period_end)} and stops all
            future charges.
          </p>
          <Button variant="danger" onClick={() => setShowCancel(true)}>
            Cancel membership
          </Button>
        </div>
      )}

      {showCancel && (
        <CancelDialog
          sub={sub}
          busy={busy === "cancel"}
          error={cancelError}
          onConfirm={confirmCancel}
          onClose={() => {
            setShowCancel(false);
            setCancelError("");
          }}
        />
      )}
    </div>
  );
}
